import styled from "styled-components";

import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../utils/constants";
import { toneColor } from "../../../../utils";
import { HeroTone } from "./types";

// The Hero is the page's summary anchor: it carries more elevation than the
// content sections below and a tightened bottom margin so the filter bar reads
// as its control strip rather than a peer card.
export const HeroCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg}px;
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px
    ${SPACING_RESPONSIVE.mobile.pageMargin}px ${SPACING.sm}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  border-radius: ${SPACING.md}px;
  background: var(--hero-background);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-medium);

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px
      ${SPACING_RESPONSIVE.tablet.pageMargin}px ${SPACING.sm}px;
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px
      ${SPACING_RESPONSIVE.desktop.pageMargin}px ${SPACING.sm}px;
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
`;

export const PeriodLabel = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

export const Balance = styled.p<{ $tone: HeroTone }>`
  margin: 0;
  font-size: var(--text-display);
  font-weight: var(--font-weight-bold);
  line-height: 1.1;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) => toneColor($tone)};

  @media ${QUERIES.tabletLargeAndUp} {
    font-size: var(--text-display-xl);
  }
`;

export const Divider = styled.hr`
  width: 100%;
  height: 0px;
  margin: 0;
  border: none;
  background: var(--color-gray-800);

  @media ${QUERIES.tabletLargeAndUp} {
    height: 1px;
  }
`;

export const StatsRow = styled.div`
  display: flex;
  flex-direction: column;

  @media ${QUERIES.tabletLargeAndUp} {
    flex-direction: row;
  }
`;

export const Stat = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  min-width: 0;
  padding: ${SPACING.sm}px 0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:first-child) {
    border-top: 1px solid var(--color-gray-800);
  }

  @media ${QUERIES.tabletLargeAndUp} {
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: ${SPACING.xs}px;
    padding: 0 ${SPACING.lg}px;

    &:first-child {
      padding-left: 0;
    }

    &:not(:first-child) {
      border-top: none;
      border-left: 1px solid var(--color-gray-800);
    }
  }
`;

export const StatLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

export const StatValue = styled.span<{ $tone: HeroTone }>`
  font-size: var(--text-md);
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) => toneColor($tone)};

  @media ${QUERIES.tabletAndUp} {
    font-size: var(--text-lg);
  }
`;

export const EmptyCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.lg}px;
  padding: ${SPACING.xl}px ${SPACING.lg}px;
  border: 1px dashed var(--empty-state-border);
  border-radius: ${SPACING.md}px;
  background: var(--empty-state-background);
  text-align: center;

  @media ${QUERIES.tabletAndUp} {
    flex-direction: row;
    gap: ${SPACING.md}px;
    text-align: left;
  }
`;

export const EmptyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.sm}px;

  @media ${QUERIES.tabletAndUp} {
    flex-direction: row;
    gap: ${SPACING.md}px;
  }
`;

export const EmptyIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--empty-state-icon-color);

  & > svg {
    font-size: var(--text-display);
  }
`;

export const EmptyText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const EmptyTitle = styled.h2`
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const EmptyDescription = styled.p`
  margin: 0;
  font-size: var(--text-base);
  color: var(--color-text-muted);
`;
